import { Customer } from "@jugl-web/rest-api";
import { useMemo } from "react";
import { convertSnakeCaseToReadableString } from "@jugl-web/utils";
import { customerPhoneStringToDisplay } from "@jugl-web/domain-resources/customers/utils/customerPhoneStringToDisplay";
import { useCustomerFields } from "./useCustomerFields";

type GroupedRow = {
  id: string;
  nameId: string;
  label: string;
  value: string;
  isHidden: boolean;
  visibility?: boolean;
  section: string;
  pos: number;
};

export const useCustomerData = (customer: Customer | null) => {
  const { getFieldDefinition } = useCustomerFields({
    entityId: customer?.entity_id,
  });
  const firstName = customer?.fields.find(
    (field) =>
      field.field.section === "personal" && field.field.name === "first_name"
  )?.value;

  const lastName = customer?.fields.find(
    (field) =>
      field.field.section === "personal" && field.field.name === "last_name"
  )?.value;

  const customerName = [firstName, lastName].filter(Boolean).join(" ");
  const companyName = customer?.fields.filter(
    (field) =>
      field.field.section === "company" && field.field.name === "company_name"
  )[0]?.value;
  const attributes = useMemo(() => {
    if (!customer) return [];
    const { fields } = customer;
    const groupNames = ["country", "city", "state"];
    const sectionOrder = ["personal", "business"];
    const sortedFields = [...fields].sort((a, b) => {
      const sectionA =
        sectionOrder.indexOf(a.field.section) === -1
          ? 99
          : sectionOrder.indexOf(a.field.section);
      const sectionB =
        sectionOrder.indexOf(b.field.section) === -1
          ? 99
          : sectionOrder.indexOf(b.field.section);
      if (sectionA !== sectionB) return sectionA - sectionB;
      return (a.field.pos ?? 0) - (b.field.pos ?? 0);
    });

    const sections = Array.from(
      new Set(sortedFields.map(({ field }) => field.section || "default"))
    );

    const groupedRows: GroupedRow[] = sections
      .map((section) => {
        const grouped = sortedFields.filter(
          ({ field }) =>
            groupNames.includes(field.name) &&
            (field.section || "default") === section
        );

        const presentNames = groupNames.filter((name) =>
          grouped.some(({ field, value }) => field.name === name && !!value)
        );

        if (presentNames.length === 0) return null;

        const values = presentNames
          .map(
            (name) => grouped.find(({ field }) => field.name === name)?.value
          )
          .filter(Boolean)
          .join(", ");

        const label = presentNames
          .map((name) => convertSnakeCaseToReadableString(name))
          .join(", ")
          ?.replace(/, ([^,]*)$/, " & $1");

        return {
          id: `country-city-state-${section}`,
          nameId: `country-city-state-${section}`,
          label,
          value: values,
          isHidden: values.length === 0,
          visibility: grouped.every(({ field }) => {
            const fieldDefinition = getFieldDefinition(field.id);
            return fieldDefinition?.visibility;
          }),
          section,
          pos: Math.min(...grouped.map(({ field }) => field.pos ?? 0)),
        };
      })
      .filter(Boolean) as GroupedRow[];

    const groupedFieldIds = new Set(
      sections.flatMap((section) =>
        groupNames.map(
          (name) =>
            sortedFields.find(
              ({ field }) =>
                field.name === name && (field.section || "default") === section
            )?.field.id
        )
      )
    );

    const mappedOthers: GroupedRow[] = sortedFields
      .filter(({ field }) => !groupedFieldIds.has(field.id))
      .map(({ field, value }) => {
        if (field.type === "mobile" && value) {
          value = customerPhoneStringToDisplay(value);
        }
        const fieldDefinition = getFieldDefinition(field.id);
        const isDropdown = fieldDefinition?.type === "dropdown";
        const selectedDropdownValue =
          fieldDefinition?.values?.find((v) => v.id === value)?.value || null;
        return {
          id: field.id,
          nameId: field.name,
          label: convertSnakeCaseToReadableString(field.name),
          value: isDropdown ? selectedDropdownValue : value,
          isHidden: isDropdown ? !selectedDropdownValue : !value,
          visibility: fieldDefinition?.visibility,
          section: field.section,
          pos: field.pos ?? 0,
        };
      })
      .filter(Boolean) as GroupedRow[];

    /**
     * Returns a sorting weight for a section.
     * Sections defined in `sectionOrder` get a lower weight (appearing first).
     * Other sections or items without a section get a high weight (appearing last).
     */
    const getSectionSortWeight = (item: GroupedRow) => {
      // If the item doesn't exist or has no section, place it at the end.
      if (!item?.section) {
        return 999;
      }

      const index = sectionOrder.indexOf(item.section);

      // If the section is not in the `sectionOrder` list, place it at the end.
      // Otherwise, use its index as the weight.
      return index === -1 ? 999 : index;
    };

    const allRows = [...groupedRows, ...mappedOthers].sort((a, b) => {
      // 1. Sort by section weight (e.g., "personal" before "business").
      const sectionWeightA = getSectionSortWeight(a);
      const sectionWeightB = getSectionSortWeight(b);

      if (sectionWeightA !== sectionWeightB) {
        return sectionWeightA - sectionWeightB;
      }

      // 2. If sections are the same, sort by position (`pos`).
      const positionA = a?.pos ?? 0;
      const positionB = b?.pos ?? 0;
      return positionA - positionB;
    });

    return allRows;
  }, [customer, getFieldDefinition]);

  return { attributes, customerName, companyName };
};
